import axios, { AxiosResponse } from "axios";
import { getEnvVar } from "ideas.env";
import { JsonValue } from "type-fest";
import { ToolSpecV3 } from "types/json-schemas/ToolSpecV3";
import { getRequestHeaders } from "utils/getRequestHeaders";

export class InvalidBundleError extends Error {}

/**
 * Reads a bundle file and returns bundle version and raw metadata
 * @param file File instance
 * @throws if not a valid bundle
 * @returns  bundle version and raw metadata content from header
 */
const readBundle = async (file: File) => {
  const BUNDLE_MAGIC = "CBUNDLE\x00"; // Magic string, padded to 8 bytes
  const BUNDLE_HEADER_SIZE = 256;
  const magic = await file.slice(0, 8).text();
  if (magic !== BUNDLE_MAGIC) {
    throw new InvalidBundleError();
  }

  const bundleHeaderDataView = new DataView(
    await file.slice(0, BUNDLE_HEADER_SIZE).arrayBuffer(),
  );
  const version = bundleHeaderDataView.getBigUint64(8, true);
  const metadataLength = bundleHeaderDataView.getBigUint64(16, true);

  // Checksum is fixed 16 bytes after metadata length
  const checksum = new Uint8Array(bundleHeaderDataView.buffer, 24, 16);
  const checksumHex = Array.from(checksum)
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");

  const headerPlusMetadata = Number(
    BigInt(BUNDLE_HEADER_SIZE) + metadataLength,
  );
  const metadata = JSON.parse(
    await file.slice(BUNDLE_HEADER_SIZE, headerPlusMetadata).text(),
  ) as Record<
    string,
    | {
        config: unknown;
        tool_spec: {
          params: ToolSpecV3["params"];
          results: ToolSpecV3["results"];
        };
      }
    | undefined
  >;

  /**
   * Slice file contents from header so only file contents are uploaded
   */

  const bundleSlice = file.slice(headerPlusMetadata, file.size);
  return {
    bundleVersion: version,
    metadata,
    bundleCodeSlice: new File([bundleSlice], file.name),
    checksum: checksumHex,
  };
};

export type BundleValidationResponse =
  | { is_valid: true; is_compatible: true }
  | { is_valid: false; error: string }
  | {
      is_valid: true;
      is_compatible: false;
      migration_details: { key: string; detail: string }[];
    };

/**
 * Backend Endpoint to validate bundle structure and compatibility
 * @param file File
 * @param toolVersionId
 * @returns information about whether bundle is valid and compatible
 * with existing spec, and information about how the backend will resolve
 * incompatibilities
 */
export const validateBundle = async (
  file: File,
  toolVersionId: number,
): Promise<
  BundleValidationResponse & {
    metadata: JsonValue;
    bundleCodeSlice: File;
    checksum: string;
  }
> => {
  const headers = await getRequestHeaders();
  const { metadata, bundleVersion, bundleCodeSlice, checksum } =
    await readBundle(file);

  const res = await axios.post<
    {
      new_bundle_metadata: unknown;
      bundle_version: number;
    },
    AxiosResponse<BundleValidationResponse>
  >(
    getEnvVar("URL_TRS_TOOL_VERSION_COMPATIBLE")(toolVersionId),
    {
      new_bundle_metadata: metadata,
      bundle_version: Number(bundleVersion),
    },
    { headers },
  );
  /**
   * For testing, uncomment the block below to mock incompatible bundle response from backend
   */

  // return {
  //   is_valid: true,
  //   is_compatible: false,
  //   errors: [
  //     {
  //       param_key: "num_cells",
  //       error:
  //         "This has changed from string to int. This parameter will be overwritten.",
  //     },
  //     {
  //       param_key: "oops_extra_parameter",
  //       error:
  //         "This parameter has been deleted from your function. It will be deleted from your parameter configuration.",
  //     },
  //   ],
  //   metadata: metadata as JsonValue,
  // };

  return {
    ...res.data,
    metadata: metadata as JsonValue,
    bundleCodeSlice,
    checksum,
  };
};
